import {
  CreateOrderDraftRequest,
  CreateOrderRequest,
  FinalizeDraftOrderRequest,
  GarmentType,
  Order,
  OrderFilterOptions,
  OrderStatistics,
  OrderStatus,
  OrderSubStatus,
  SaveDraftGarmentsRequest,
  UpdateOrderRequest
} from '@/types/orders'
import { createAuthenticatedApi, extractApiErrorMessage } from '@/lib/api'
import { getDefaultSubGarmentType } from '@/utils/orders/garmentConfig'

type BackendOrderStatus = 'NEW' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'
type BackendOrderSubStatus = 'PENDING' | 'IN_PRODUCTION' | 'READY_FOR_TRIAL' | 'READY_FOR_DELIVERY' | 'DELIVERED' | null
type BackendGarmentType = 'JACKET_VEST' | 'SHERWANI' | 'SHIRT' | 'KURTA' | 'TROUSER' | 'KAFTAN' | 'ALTERATION'

interface BackendOrder {
  id: string
  orderNumber: string
  isDraft?: boolean
  customerId: string
  branchId: string
  employeeId?: string | null
  masterName?: string | null
  salesmanName: string
  orderDate: string
  deliveryDate?: string | null
  trialDate1?: string | null
  trialDate2?: string | null
  status: BackendOrderStatus
  subStatus?: BackendOrderSubStatus
  totalAmount?: string | number | null
  advanceAmount?: string | number | null
  balanceAmount?: string | number | null
  invoiceNumber?: string | null
  deliveryAddress?: string | null
  notes?: string | null
  createdAt: string
  updatedAt: string
  customer?: {
    id: string
    name: string
    personalMobile?: string | null
    email?: string | null
    address?: string | null
    city?: string | null
    country?: string | null
    deliveryAddress?: string | null
  }
  branch?: {
    id: string
    branchName: string
    branchCode: string
  }
  employee?: {
    id: string
    fullName: string
    designation: string
  } | null
  garments?: Array<{
    id: string
    type: BackendGarmentType
    subGarmentType?: string | null
    quantity: number
    description?: string | null
    remarks?: string | null
    customerProvidedFabric?: boolean | null
    fabricBrand?: string | null
    fabricCode?: string | null
    fabricQuantity?: string | number | null
  }>
  noteEntries?: Array<{
    id: string
    orderId: string
    note: string
    createdByEmployeeId?: string | null
    createdByName: string
    createdAt: string
    updatedAt: string
  }>
}

interface OrderListApiResponse {
  success: boolean
  data: BackendOrder[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

interface OrderApiResponse {
  success: boolean
  data: BackendOrder
}

interface FinalizeDraftOrderApiResponse {
  success: boolean
  data: {
    id: string
    orderNumber: string | null
  }
}

const api = createAuthenticatedApi()
const DEFAULT_ORDER_LIST_LIMIT = 1000

const backendToFrontendGarmentMap: Record<BackendGarmentType, GarmentType> = {
  JACKET_VEST: GarmentType.JACKET_VEST,
  SHERWANI: GarmentType.SHERWANI,
  SHIRT: GarmentType.SHIRT,
  KURTA: GarmentType.KURTA,
  TROUSER: GarmentType.TROUSER,
  KAFTAN: GarmentType.KAFTAN,
  ALTERATION: GarmentType.ALTERATION
}

const frontendToBackendGarmentMap: Record<GarmentType, BackendGarmentType> = {
  [GarmentType.JACKET_VEST]: 'JACKET_VEST',
  [GarmentType.SHERWANI]: 'SHERWANI',
  [GarmentType.SHIRT]: 'SHIRT',
  [GarmentType.KURTA]: 'KURTA',
  [GarmentType.TROUSER]: 'TROUSER',
  [GarmentType.KAFTAN]: 'KAFTAN',
  [GarmentType.ALTERATION]: 'ALTERATION'
}

const mapBackendStatus = (status: BackendOrderStatus, subStatus?: BackendOrderSubStatus): OrderStatus => {
  if (status === 'COMPLETED' && subStatus === 'DELIVERED') {
    return OrderStatus.DELIVERED
  }

  switch (status) {
    case 'NEW':
      return OrderStatus.NEW
    case 'IN_PROGRESS':
      return OrderStatus.IN_PROGRESS
    case 'COMPLETED':
      return OrderStatus.COMPLETED
    case 'CANCELLED':
      return OrderStatus.CANCELLED
    default:
      return OrderStatus.NEW
  }
}

const mapBackendSubStatus = (status: BackendOrderStatus, subStatus?: BackendOrderSubStatus): OrderSubStatus | undefined => {
  if (!subStatus || subStatus === 'PENDING') {
    return undefined
  }

  if (status === 'COMPLETED' && subStatus === 'DELIVERED') {
    return undefined
  }

  switch (subStatus) {
    case 'IN_PRODUCTION':
      return OrderSubStatus.IN_PRODUCTION
    case 'READY_FOR_TRIAL':
      return OrderSubStatus.READY_FOR_TRIAL
    case 'READY_FOR_DELIVERY':
      return OrderSubStatus.READY_FOR_DELIVERY
    case 'DELIVERED':
      return OrderSubStatus.DELIVERED
    default:
      return undefined
  }
}

const mapFrontendStatus = (status: OrderStatus, subStatus?: OrderSubStatus): {
  status: BackendOrderStatus
  subStatus?: BackendOrderSubStatus
} => {
  if (status === OrderStatus.DELIVERED || subStatus === OrderSubStatus.DELIVERED) {
    return { status: 'COMPLETED', subStatus: 'DELIVERED' }
  }

  switch (status) {
    case OrderStatus.NEW:
      return { status: 'NEW', subStatus: 'PENDING' }
    case OrderStatus.IN_PROGRESS:
      return {
        status: 'IN_PROGRESS',
        subStatus:
          subStatus === OrderSubStatus.READY_FOR_TRIAL
            ? 'READY_FOR_TRIAL'
            : subStatus === OrderSubStatus.IN_PRODUCTION
              ? 'IN_PRODUCTION'
              : 'PENDING'
      }
    case OrderStatus.COMPLETED:
      return {
        status: 'COMPLETED',
        subStatus: subStatus === OrderSubStatus.READY_FOR_DELIVERY ? 'READY_FOR_DELIVERY' : 'PENDING'
      }
    case OrderStatus.CANCELLED:
      return { status: 'CANCELLED' }
    default:
      return { status: 'NEW', subStatus: 'PENDING' }
  }
}

const toNumber = (value?: string | number | null): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  return typeof value === 'number' ? value : Number(value)
}

const extractFabricQuantity = (value?: string | number | null, description?: string | null): string | undefined => {
  if (value !== null && value !== undefined && value !== '') {
    return String(value)
  }

  if (!description) {
    return undefined
  }

  const match = description.match(/Fabric Qty:\s*([0-9]+(?:\.[0-9]+)?)/i)
  return match?.[1]
}

export const mapBackendOrderToFrontend = (order: BackendOrder): Order => ({
  id: order.id,
  orderId: order.orderNumber,
  isDraft: order.isDraft ?? false,
  customerId: order.customerId,
  customerName: order.customer?.name || '',
  customerContact: order.customer?.personalMobile || '',
  customerEmail: order.customer?.email || '',
  customerAddress: order.customer?.address || '',
  customerCity: order.customer?.city || '',
  customerCountry: order.customer?.country || '',
  deliveryAddress: order.deliveryAddress || order.customer?.deliveryAddress || order.customer?.address || '',
  branchId: order.branchId,
  branchName: order.branch?.branchName,
  salesmanId: order.employeeId || '',
  salesmanName: order.salesmanName,
  masterName: order.masterName || '',
  status: mapBackendStatus(order.status, order.subStatus),
  subStatus: mapBackendSubStatus(order.status, order.subStatus),
  invoiceNumber: order.invoiceNumber || undefined,
  trialDate1: order.trialDate1 ? new Date(order.trialDate1) : undefined,
  trialDate2: order.trialDate2 ? new Date(order.trialDate2) : undefined,
  deliveryDate: order.deliveryDate ? new Date(order.deliveryDate) : undefined,
  totalAmount: toNumber(order.totalAmount),
  advanceAmount: toNumber(order.advanceAmount),
  balanceAmount: toNumber(order.balanceAmount),
  notes: order.notes || undefined,
  createdAt: new Date(order.createdAt),
  updatedAt: new Date(order.updatedAt),
  statusHistory: [],
  noteEntries: (order.noteEntries || []).map((entry) => ({
    id: entry.id,
    orderId: entry.orderId,
    note: entry.note,
    createdByEmployeeId: entry.createdByEmployeeId || undefined,
    createdByName: entry.createdByName,
    createdAt: new Date(entry.createdAt),
    updatedAt: new Date(entry.updatedAt)
  })),
  garments: (order.garments || []).map((garment) => ({
    id: garment.id,
    orderId: order.id,
    garmentType: backendToFrontendGarmentMap[garment.type],
    subGarmentType: garment.subGarmentType || getDefaultSubGarmentType(backendToFrontendGarmentMap[garment.type]) || undefined,
    quantity: garment.quantity,
    description: garment.description || undefined,
    remarks: garment.remarks || garment.description || undefined,
    customerProvidedFabric: garment.customerProvidedFabric ?? false,
    fabricBrand: garment.fabricBrand || undefined,
    fabricCode: garment.fabricCode || undefined,
    fabricQuantity: extractFabricQuantity(garment.fabricQuantity, garment.description),
    measurementId: '',
    measurementData: {
      id: '',
      customerId: order.customerId,
      garmentType: backendToFrontendGarmentMap[garment.type],
      measurements: {},
      createdAt: new Date(order.createdAt),
      updatedAt: new Date(order.updatedAt)
    },
    itemAmount: undefined
  }))
})

const calculateStatistics = (orders: Order[]): OrderStatistics => ({
  totalOrders: orders.length,
  newOrders: orders.filter((order) => order.status === OrderStatus.NEW).length,
  inProgressOrders: orders.filter((order) => order.status === OrderStatus.IN_PROGRESS).length,
  readyForTrialOrders: orders.filter((order) => order.subStatus === OrderSubStatus.READY_FOR_TRIAL).length,
  completedOrders: orders.filter((order) => order.status === OrderStatus.COMPLETED).length,
  deliveredOrders: orders.filter((order) => order.status === OrderStatus.DELIVERED).length
})

export const getOrders = async (filters?: OrderFilterOptions): Promise<{
  orders: Order[]
  statistics: OrderStatistics
}> => {
  try {
    const params = new URLSearchParams()

    if (filters?.branchId) params.append('branchId', filters.branchId)
    if (filters?.customerId) params.append('customerId', filters.customerId)
    params.append('limit', String(filters?.limit ?? DEFAULT_ORDER_LIST_LIMIT))

    const mappedStatus = filters?.status ? mapFrontendStatus(filters.status, filters.subStatus) : undefined
    if (mappedStatus?.status) params.append('status', mappedStatus.status)

    const response = await api.get<OrderListApiResponse>(`/orders?${params.toString()}`)
    const orders = response.data.data.map(mapBackendOrderToFrontend)

    return {
      orders,
      statistics: {
        ...calculateStatistics(orders),
        totalOrders: response.data.pagination.total
      }
    }
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to fetch orders'))
  }
}

export const getOrderById = async (id: string): Promise<Order> => {
  try {
    const response = await api.get<OrderApiResponse>(`/orders/${id}`)
    return mapBackendOrderToFrontend(response.data.data)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to fetch order'))
  }
}

export const createOrder = async (data: CreateOrderRequest): Promise<Order> => {
  try {
    const response = await api.post<OrderApiResponse>('/orders', {
      customerId: data.customerId,
      branchId: data.branchId,
      employeeId: data.employeeId,
      masterName: data.masterName,
      salesmanName: data.salesmanName,
      deliveryDate: data.deliveryDate.toISOString(),
      trialDate1: data.trialDate1?.toISOString(),
      trialDate2: data.trialDate2?.toISOString(),
      invoiceNumber: data.invoiceNumber,
      deliveryAddress: data.deliveryAddress,
      notes: data.notes,
      garments: data.garmentItems.map((item) => ({
        type: frontendToBackendGarmentMap[item.garmentType],
        subGarmentType: item.subGarmentType,
        quantity: item.quantity,
        description: item.description,
        remarks: item.remarks,
        customerProvidedFabric: item.customerProvidedFabric,
        fabricBrand: item.fabricBrand,
        fabricCode: item.fabricCode,
        fabricQuantity: item.fabricQuantity
      }))
    })

    return mapBackendOrderToFrontend(response.data.data)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to create order'))
  }
}

export const createOrderDraft = async (data: CreateOrderDraftRequest): Promise<Order> => {
  try {
    const response = await api.post<OrderApiResponse>('/orders/draft', {
      customerId: data.customerId,
      branchId: data.branchId,
      employeeId: data.employeeId,
      salesmanName: data.salesmanName,
      deliveryAddress: data.deliveryAddress
    })

    return mapBackendOrderToFrontend(response.data.data)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to create order draft'))
  }
}

export const saveDraftOrderGarments = async (data: SaveDraftGarmentsRequest): Promise<Order> => {
  try {
    const response = await api.put<OrderApiResponse>(`/orders/${data.orderId}/draft-garments`, {
      garments: data.garmentItems.map((item) => ({
        type: frontendToBackendGarmentMap[item.garmentType],
        subGarmentType: item.subGarmentType,
        quantity: item.quantity,
        description: item.description,
        remarks: item.remarks,
        customerProvidedFabric: item.customerProvidedFabric,
        fabricBrand: item.fabricBrand,
        fabricCode: item.fabricCode,
        fabricQuantity: item.fabricQuantity
      }))
    })

    return mapBackendOrderToFrontend(response.data.data)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to save draft garments'))
  }
}

export const finalizeDraftOrder = async (
  data: FinalizeDraftOrderRequest
): Promise<{ id: string; orderNumber: string | null }> => {
  try {
    const response = await api.post<FinalizeDraftOrderApiResponse>(`/orders/${data.id}/finalize`, {
      masterName: data.masterName,
      invoiceNumber: data.invoiceNumber,
      deliveryAddress: data.deliveryAddress,
      trialDate1: data.trialDate1?.toISOString(),
      trialDate2: data.trialDate2?.toISOString(),
      deliveryDate: data.deliveryDate.toISOString(),
      notes: data.notes
    })

    return response.data.data
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to finalize order'))
  }
}

export const discardDraftOrder = async (id: string): Promise<void> => {
  try {
    await api.delete(`/orders/${id}/draft`)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to discard draft order'))
  }
}

export const updateOrder = async (data: UpdateOrderRequest): Promise<Order> => {
  try {
    const mappedStatus = data.status ? mapFrontendStatus(data.status, data.subStatus) : undefined

    const response = await api.put<OrderApiResponse>(`/orders/${data.id}`, {
      branchId: data.branchId,
      employeeId: data.employeeId,
      masterName: data.masterName,
      salesmanName: data.salesmanName,
      garmentItems: data.garmentItems?.map((item) => ({
        id: item.id,
        type: frontendToBackendGarmentMap[item.garmentType],
        subGarmentType: item.subGarmentType,
        quantity: item.quantity,
        customerProvidedFabric: item.customerProvidedFabric,
        fabricBrand: item.fabricBrand,
        fabricCode: item.fabricCode,
        remarks: item.remarks,
        fabricQuantity: item.fabricQuantity
      })),
      deliveryDate: data.deliveryDate?.toISOString(),
      trialDate1: data.trialDate1?.toISOString(),
      trialDate2: data.trialDate2?.toISOString(),
      status: mappedStatus?.status,
      subStatus: mappedStatus?.subStatus,
      totalAmount: data.totalAmount,
      advanceAmount: data.advanceAmount,
      invoiceNumber: data.invoiceNumber,
      deliveryAddress: data.deliveryAddress,
      notes: data.notes
    })

    return mapBackendOrderToFrontend(response.data.data)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to update order'))
  }
}

export const deleteOrder = async (id: string): Promise<void> => {
  try {
    await api.delete(`/orders/${id}`)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to delete order'))
  }
}

export const updateOrderStatus = async (
  id: string,
  status: OrderStatus,
  subStatus?: OrderSubStatus
): Promise<Order> => {
  try {
    const mappedStatus = mapFrontendStatus(status, subStatus)
    const response = await api.put<OrderApiResponse>(`/orders/${id}/status`, mappedStatus)
    return mapBackendOrderToFrontend(response.data.data)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to update order status'))
  }
}
